import { useContext } from "react";
import { Helmet } from "react-helmet";
import { ThemeContext } from "../../Contaxt/ThemeContext";
import Layout from "./Layout";
import PageTitleBanner from "./PageTitleBanner";

const jaipurOffice = {
    name: "iShivax Development Headquarters",
    address: "B-8/2 Shiv Marg, Banipark, Jaipur, Rajasthan, India - 302016",
    mapUrl: "https://maps.app.goo.gl/MbG7nMkdEcRHdz3n9",
    embedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.180980185912!2d75.7970584761222!3d26.92947645936873!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db35e704ce509%3A0x21a49e8d70a4d0!2siShivax!5e0!3m2!1sen!2sin!4v1780549636130!5m2!1sen!2sin",
    phoneIndia: "+91-9829197777",
    phoneUsa: "+1 (815) 706-4781",
    phoneUsa2: "+1 (669) 669-0649",
    email: "business@ishivax.com",
};

const serviceList = [
    "Custom Software Development",
    "Mobile App Development (iOS & Android)",
    "Web Application Development",
    "IoT and Automation Solutions",
    "Digital Marketing",
    "Microsoft Technologies",
];

const clientBenefits = [
    "US LLC-based contracting for simple agreements and invoicing",
    "Cost-effective development with competitive India-based pricing",
    "Dedicated full-time development team in Jaipur",
    "Flexible working hours aligned with 10:00 AM – 7:00 PM IST or project-based schedules",
];

const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: jaipurOffice.name,
    url: "https://ishivax.com/jaipur-software-office",
    image: "https://ishivax.com/images/og-image.png",
    email: jaipurOffice.email,
    telephone: jaipurOffice.phoneIndia,
    priceRange: "$$",
    address: {
        "@type": "PostalAddress",
        streetAddress: "B-8/2 Shiv Marg, Banipark",
        addressLocality: "Jaipur",
        addressRegion: "Rajasthan",
        postalCode: "302016",
        addressCountry: "IN",
    },
    areaServed: ["Jaipur", "Rajasthan", "India", "United States"],
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "10:00",
            closes: "19:00",
        },
    ],
    contactPoint: [
        {
            "@type": "ContactPoint",
            telephone: jaipurOffice.phoneIndia,
            contactType: "development office",
            areaServed: "IN",
            availableLanguage: ["English", "Hindi"],
        },
        {
            "@type": "ContactPoint",
            telephone: jaipurOffice.phoneUsa,
            contactType: "contracts",
            areaServed: "US",
            availableLanguage: ["English"],
        },
    ],
    makesOffer: serviceList.map((service) => ({
        "@type": "Offer",
        itemOffered: {
            "@type": "Service",
            name: service,
        },
    })),
};

const LocationPage = () => {
    const { isLight } = useContext(ThemeContext);

    return (
        <Layout>
            <Helmet>
                <title>Our Development Headquarters in Jaipur, Rajasthan</title>
                <meta
                    name="description"
                    content="Visit iShivax development headquarters in Jaipur, Rajasthan. Our software development, mobile app, IoT, web application, and IT services are delivered from Banipark, Jaipur."
                />
                <meta
                    name="keywords"
                    content="software company in Jaipur, Jaipur software office, app development company Jaipur, IoT solutions Jaipur, iShivax Jaipur office"
                />
                <link rel="canonical" href="https://ishivax.com/jaipur-software-office" />
                <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
            </Helmet>

            <PageTitleBanner kicker="Location" />

            <main className={`location-page ${isLight ? "location-page-light" : "location-page-dark"}`}>
                <section className="location-hero location-hero-compact">
                    <div className="location-shell">
                        <p className="location-intro">
                            All iShivax software development, app creation, IoT solutions, and IT services
                            are delivered from our dedicated team based in Jaipur.
                        </p>
                        <div className="location-actions">
                            <a className="location-call-india" href="tel:+919829197777">Call India</a>
                            <a href="mailto:business@ishivax.com">Email Team</a>
                            <a href={jaipurOffice.mapUrl} target="_blank" rel="noreferrer">Open Map</a>
                        </div>
                    </div>
                </section>

                <section className="location-shell location-map-feature">
                    <div className="location-card location-office-card">
                        <p className="location-card-label">Our Jaipur Office</p>
                        <h2>Where The Work Happens</h2>
                        <address>{jaipurOffice.address}</address>
                        <p className="location-card-note">
                            Our entire development team works from this location. This is where your
                            projects are built, managed, and delivered.
                        </p>
                        <iframe
                            title="iShivax Jaipur software office map"
                            src={jaipurOffice.embedUrl}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </section>

                <section className="location-shell location-content-grid">
                    <article className="location-info-panel">
                        <p className="location-card-label">Global Clients</p>
                        <h2>Delivering Digital Solutions to Clients Worldwide from Jaipur</h2>
                        <p>
                            We have a registered office in Austin, Texas, USA for legal and contracting purposes. However, all development work is handled by our expert team based in Jaipur, India.
                        </p>
                        <h3>What this means for you:</h3>
                        <ul className="location-check-list">
                            {clientBenefits.map((benefit, index) => (
                                <li key={benefit}>{index + 1}. {benefit}</li>
                            ))}
                        </ul>
                    </article>

                    <article className="location-info-panel">
                        <p className="location-card-label">Services</p>
                        <h2>Services Delivered from Our Jaipur Office</h2>
                        <p>We provide end-to-end digital solutions from our Jaipur development center, serving clients across the globe.</p>
                        <ul className="location-service-list">
                            {serviceList.map((service, index) => (
                                <li key={index}>
                                    {index + 1}. {service}
                                </li>
                            ))}
                        </ul>
                    </article>
                </section>

                <section className="location-shell location-contact-panel">
                    <div>
                        <p className="location-card-label">Contact</p>
                        <h2>Contact Our Development Team</h2>
                    </div>
                    <div className="location-contact-list">
                        <a href="tel:+919829197777">
                            <b>India :</b> {jaipurOffice.phoneIndia}
                        </a>

                        <div className="location-usa-contacts">
                            <b>USA</b>
                            <b className="location-card-samicolon">:</b>

                            <div className="location-usa-links">
                                <a href="tel:+18157064781">{jaipurOffice.phoneUsa}</a>
                                <span>|</span>
                                <a href="tel:+16696690649">{jaipurOffice.phoneUsa2}</a>
                            </div>
                        </div>

                        <a href="mailto:business@ishivax.com">
                            <b>Email:</b> {jaipurOffice.email}
                        </a>

                        <p>
                            <b>Visit our Jaipur office:</b> {jaipurOffice.address}
                        </p>

                        <p>
                            <b>Office Hours:</b> Monday-Saturday, 10 AM to 7 PM IST
                        </p>
                    </div>
                </section>
            </main>
        </Layout>
    );
};

export default LocationPage;
